import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Camera, ArrowLeft, MapPin, Search } from 'lucide-react';

const NotFound = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const quickLinks = [
    {
      icon: Home,
      title: currentLang === 'de' ? 'Startseite' : 'Главная',
      description: currentLang === 'de' 
        ? 'Zurück zur Hauptseite mit Immobilienfotografie-Services'
        : 'Вернуться на главную страницу с услугами фотографии недвижимости',
      path: '/'
    },
    {
      icon: Camera,
      title: currentLang === 'de' ? 'Portfolio' : 'Портфолио',
      description: currentLang === 'de'
        ? 'Sehen Sie unsere Immobilienfotografie-Arbeiten'
        : 'Посмотреть наши работы по фотографии недвижимости',
      path: '/portfolio'
    },
    {
      icon: MapPin,
      title: currentLang === 'de' ? 'Standorte' : 'Локации',
      description: currentLang === 'de'
        ? 'Immobilienfotografie in Gera, Ronneburg und anderen Städten'
        : 'Фотография недвижимости в Гере, Роннебурге и других городах',
      path: '/gera'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 border border-white rotate-45"></div>
        <div className="absolute top-40 right-20 w-24 h-24 border border-white rotate-12"></div>
        <div className="absolute bottom-32 left-1/4 w-20 h-20 border border-white rotate-45"></div>
        <div className="absolute bottom-20 right-10 w-28 h-28 border border-white -rotate-12"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 py-20">
        
        {/* 404 Animation */}
        <div className={`transition-all duration-1000 ${mounted ? 'transform scale-100 opacity-100' : 'transform scale-50 opacity-0'}`}>
          <div className="text-center mb-8 mt-20">
            <div className="mb-8">
              <h1 
                className="text-6xl md:text-7xl lg:text-8xl font-bold mb-4 tracking-wider relative"
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                <span className="relative z-10">404</span>
                <div className="absolute inset-0 text-gray-800 transform translate-x-2 translate-y-2 -z-10">404</div>
              </h1>
            </div>
            
            {/* Camera Icon */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <Camera className="w-16 h-16 text-gray-400" />
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                  <Search className="w-3 h-3 text-white" />
                </div>
              </div>
            </div>

            <h2 
              className="text-2xl md:text-3xl font-bold mb-3"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {currentLang === 'de' ? 'Seite nicht gefunden' : 'Страница не найдена'}
            </h2>
            
            <p className="text-lg text-gray-400 max-w-xl mx-auto mb-6 leading-relaxed">
              {currentLang === 'de' 
                ? 'Wie ein perfekter Immobilienfoto-Winkel - manchmal ist das, was wir suchen, nicht dort, wo wir es erwarten. Diese Seite existiert nicht, aber unsere Leidenschaft für Immobilienfotografie ist immer da.'
                : 'Как идеальный ракурс для фотографии недвижимости - иногда то, что мы ищем, находится не там, где мы ожидаем. Эта страница не существует, но наша страсть к фотографии недвижимости всегда с вами.'
              }
            </p>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className={`transition-all duration-1000 delay-300 ${mounted ? 'transform translate-y-0 opacity-100' : 'transform translate-y-8 opacity-0'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8">
            {quickLinks.map((link, index) => {
              const IconComponent = link.icon;
              return (
                <Link
                  key={index}
                  to={link.path}
                  className="group p-4 bg-gray-900 border border-gray-800 hover:border-gray-600 transition-all duration-300 hover:bg-gray-800 transform hover:-translate-y-1"
                >
                  <div className="flex flex-col items-center text-center">
                    <IconComponent className="w-6 h-6 text-gray-400 group-hover:text-white transition-colors duration-300 mb-3" />
                    <h3 
                      className="text-base font-semibold mb-2 group-hover:text-white transition-colors duration-300"
                      style={{ fontFamily: '"Times New Roman", serif' }}
                    >
                      {link.title}
                    </h3>
                    <p className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors duration-300">
                      {link.description}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Back Button */}
        <div className={`transition-all duration-1000 delay-500 ${mounted ? 'transform translate-y-0 opacity-100' : 'transform translate-y-8 opacity-0'}`}>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-8 py-4 bg-white text-black font-semibold hover:bg-gray-200 transition-all duration-300 transform hover:scale-105"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              {currentLang === 'de' ? 'Zurück' : 'Назад'}
            </button>
            
            <Link
              to="/"
              className="inline-flex items-center px-8 py-4 border border-white text-white font-semibold hover:bg-white hover:text-black transition-all duration-300 transform hover:scale-105"
            >
              <Home className="mr-2 h-5 w-5" />
              {currentLang === 'de' ? 'Zur Startseite' : 'На главную'}
            </Link>
          </div>
        </div>

        {/* Footer Text */}
        <div className={`transition-all duration-1000 delay-700 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-center mt-8">
            <p className="text-gray-500 text-sm">
              {currentLang === 'de' 
                ? 'FotografX - Professionelle Immobilienfotografie in Thüringen'
                : 'FotografX - Профессиональная фотография недвижимости в Тюрингии'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Subtle parallax effect */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(45deg); }
          50% { transform: translateY(-10px) rotate(45deg); }
        }
        .absolute.inset-0.opacity-5 > div:nth-child(1) {
          animation: float 6s ease-in-out infinite;
        }
        .absolute.inset-0.opacity-5 > div:nth-child(2) {
          animation: float 8s ease-in-out infinite reverse;
        }
        .absolute.inset-0.opacity-5 > div:nth-child(3) {
          animation: float 7s ease-in-out infinite;
        }
        .absolute.inset-0.opacity-5 > div:nth-child(4) {
          animation: float 9s ease-in-out infinite reverse;
        }
      `}</style>
    </div>
  );
};

export default NotFound;